import { observable } from 'mobx';
import MapView from '@arcgis/core/views/MapView';
import Map from '@arcgis/core/Map';
import Polyline from '@arcgis/core/geometry/Polyline';
import Point from '@arcgis/core/geometry/Point';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import Graphic from '@arcgis/core/Graphic';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol';
import { MapViewInputTask } from 'cd-eft/components/Design/BreaklineTools/framework/MapViewInputTask';

export class StoryModel {
  readonly mapView: MapView;

  readonly tools: Tool[] = [];

  readonly activeToolSlot: ToolSlot;

  constructor() {
    const map = new Map({ basemap: 'hybrid' });
    const mapView: MapView = new MapView({
      map: map,
      center: [-95, 40], // CONTINENTAL US
      zoom: 4, // CONTINENTAL US
    });

    this.mapView = mapView;

    this.activeToolSlot = new ToolSlot();

    const pointTool = new DrawPointTool(this.mapView, this.activeToolSlot);
    const polylineTool = new DrawPolylineTool(this.mapView, this.activeToolSlot);
    this.tools.push(pointTool);
    this.tools.push(polylineTool);
  }
}

export class ToolSlot {
  @observable current: Tool | undefined;
}

export type ToolMode = 'single' | 'continuous';

export abstract class Tool {
  readonly title: string;
  private readonly mode: ToolMode;
  private readonly toolSlot: ToolSlot;

  constructor(title: string, mode: ToolMode, toolSlot: ToolSlot) {
    this.title = title;
    this.mode = mode;
    this.toolSlot = toolSlot;
  }

  get isActive(): boolean {
    return this.toolSlot.current === this;
  }

  async toggleActivated(): Promise<void> {
    if (this.isActive) {
      await this.deactivate();
    } else {
      await this.activate();
    }
  }

  async deactivate(): Promise<void> {
    if (this.isActive) {
      this.toolSlot.current = undefined;
      await this.onCancelOperation();
      // console.log('Deactivated', this.title);
    }
  }

  async activate(): Promise<void> {
    if (this.toolSlot.current) {
      await this.toolSlot.current.deactivate();
    }
    this.toolSlot.current = this;
    // console.log('Activated', this.title);

    try {
      if (this.mode === 'single') {
        await this.onExecuteOperation();
      } else if (this.mode === 'continuous') {
        while (this.mode === 'continuous') {
          await this.onExecuteOperation();
        }
      }
    } catch (error) {
      if (error && (error as Error).message === 'Input task cancelled.') {
        // do nothing, this is expected
      } else {
        throw error;
      }
    } finally {
      await this.deactivate();
    }
  }

  protected async onExecuteOperation(): Promise<void> { }

  protected async onCancelOperation(): Promise<void> { }
}

export class DrawPointTool extends Tool {
  private readonly mapView: MapView;
  private task: DrawPointInputTask;

  constructor(mapView: MapView, toolSlot: ToolSlot) {
    super('Draw Point', 'continuous', toolSlot);
    this.mapView = mapView;
    this.task = new DrawPointInputTask(this.mapView);
  }

  protected async onExecuteOperation(): Promise<void> {
    const result = await this.task.executeTaskAsync();
    // console.log(result);
    return;
  }

  protected async onCancelOperation(): Promise<void> {
    if (this.task.isExecuting) {
      await this.task.cancelTaskAsync();
    }
    return;
  }
}

export class DrawPolylineTool extends Tool {
  private readonly mapView: MapView;
  private task: DrawPolylineInputTask;

  constructor(mapView: MapView, tooSlot: ToolSlot) {
    super('Draw Polyline', 'continuous', tooSlot);
    this.mapView = mapView;
    this.task = new DrawPolylineInputTask(this.mapView);
  }

  protected async onExecuteOperation(): Promise<void> {
    const result = await this.task.executeTaskAsync();
    // console.log(result);
    return;
  }

  protected async onCancelOperation(): Promise<void> {
    if (this.task.isExecuting) {
      await this.task.cancelTaskAsync();
    }
    return;
  }
}

export class DrawPointInputTask extends MapViewInputTask<Point> {
  private readonly feedbackLayer: GraphicsLayer = new GraphicsLayer({ title: 'feedbackLayer' });

  constructor(mapView: MapView) {
    super(mapView);
  }

  protected onSetup(): void {
    this.cursor = 'crosshair';
    this.feedbackLayer.removeAll();
    this.mapView.map.add(this.feedbackLayer);
  }
  protected onCleanup(): void {
    this.mapView.map.remove(this.feedbackLayer);
    this.feedbackLayer.removeAll();
  }

  protected onImmediateClick(e: __esri.ViewImmediateClickEvent): void {
    const result = e.mapPoint;
    this.completeNow(result);
  }

  protected onPointerMove(e: __esri.ViewPointerMoveEvent): void {
    this.feedbackLayer.removeAll();

    const mapPoint = this.mapView.toMap(e);
    const symbol = new SimpleMarkerSymbol();
    const graphic = new Graphic({ geometry: mapPoint, symbol: symbol });
    this.feedbackLayer.add(graphic);
  }
}

export class DrawPolylineInputTask extends MapViewInputTask<Polyline> {
  private readonly feedbackLayer: GraphicsLayer = new GraphicsLayer({ title: 'feedbackLayer' });
  private _path?: Point[];

  constructor(mapView: MapView) {
    super(mapView);
  }

  protected onSetup(): void {
    this.cursor = 'crosshair';
    this._path = [];
    this.feedbackLayer.removeAll();
    this.mapView.map.add(this.feedbackLayer);
  }

  protected onCleanup(): void {
    this._path = undefined;
    this.mapView.map.remove(this.feedbackLayer);
    this.feedbackLayer.removeAll();
  }

  protected onImmediateClick(e: __esri.ViewImmediateClickEvent): void {
    if (this._path) {
      this._path.push(e.mapPoint);
    }
  }

  protected onImmediateDoubleClick(e: __esri.ViewImmediateDoubleClickEvent): void {
    if (this.isExecuting) {
      e.stopPropagation();
    }
    if (this._path) {
      const result = new Polyline({ spatialReference: this.mapView.spatialReference });
      result.addPath(this._path);
      this.completeNow(result);
    }
  }

  protected onPointerMove(e: __esri.ViewPointerMoveEvent): void {
    if (this._path) {
      this.feedbackLayer.removeAll();
      const mapPoint = this.mapView.toMap(e);

      const path = [...this._path];
      path.push(mapPoint);
      const polyline = new Polyline({ spatialReference: this.mapView.spatialReference });
      polyline.addPath(path);
      const lineSymbol = new SimpleLineSymbol();
      const lineGraphic = new Graphic({ geometry: polyline, symbol: lineSymbol });
      this.feedbackLayer.add(lineGraphic);

      const markerSymbol = new SimpleMarkerSymbol();
      const pointGraphic = new Graphic({ geometry: mapPoint, symbol: markerSymbol });
      this.feedbackLayer.add(pointGraphic);
    }
  }
}
